import { useEffect, useState } from "react";
import { Flame } from "@phosphor-icons/react";
import { teleproApi } from "../../lib/teleproApi";

/**
 * StreakBadge — Vague 9.
 *
 * Small motivational badge shown in the portal header. Polls
 * /me/streak on mount + when `refreshKey` changes.
 *
 *   0 days  → hidden (no shame for new téléspros)
 *   1-2 d   → grey  "🔥 1 jour"
 *   3-6 d   → amber "🔥 5 jours d'affilée"
 *   7-14 d  → orange "🔥🔥 12 jours"
 *   15+ d   → red   "🔥🔥🔥 21 jours ! Nouveau record"
 *
 * A different flavour when the télépro hasn't worked TODAY yet — a gentle
 * nudge : "N jours d'affilée · continue aujourd'hui".
 */
export function StreakBadge({ refreshKey = 0 }) {
  const [streak, setStreak] = useState(null);

  useEffect(() => {
    let dead = false;
    teleproApi.get("/me/streak")
      .then((r) => { if (!dead) setStreak(r.data); })
      .catch(() => { if (!dead) setStreak(null); });
    return () => { dead = true; };
  }, [refreshKey]);

  if (!streak || (streak.current_streak || 0) === 0) return null;
  const n = streak.current_streak;
  const isRecord = streak.longest_streak === n && n >= 5;

  let flames = "🔥";
  let bg = "bg-muted/50";
  let border = "border-border";
  let text = "text-foreground";
  if (n >= 15) {
    flames = "🔥🔥🔥"; bg = "bg-red-500/10";
    border = "border-red-500/50"; text = "text-red-700";
  } else if (n >= 7) {
    flames = "🔥🔥"; bg = "bg-orange-500/10";
    border = "border-orange-500/50"; text = "text-orange-800";
  } else if (n >= 3) {
    flames = "🔥"; bg = "bg-secondary/10";
    border = "border-secondary/40"; text = "text-secondary";
  }

  const label = n === 1 ? "jour" : "jours";

  return (
    <div
      className={`inline-flex items-center gap-1.5 border rounded-sm px-2 py-1 text-xs font-semibold ${bg} ${border} ${text}`}
      data-testid="telepro-streak-badge"
      title={`Série de ${n} ${label} consécutifs${streak.worked_today ? " (aujourd'hui compris)" : " — travaille aujourd'hui pour la faire durer !"}. Record : ${streak.longest_streak}.`}
    >
      <span aria-hidden="true">{flames}</span>
      <span>
        <b className="tabular-nums">{n}</b> {label}
        {n > 1 && <span className="hidden sm:inline"> d&apos;affilée</span>}
      </span>
      {isRecord && (
        <span className="ml-1 bg-yellow-400/30 text-yellow-800 border border-yellow-500/40 rounded-sm px-1 py-0.5 text-[9px] uppercase tracking-wider font-bold">
          Record
        </span>
      )}
      {!streak.worked_today && (
        <span className="ml-1 text-[10px] font-normal italic opacity-80">
          · continue aujourd&apos;hui
        </span>
      )}
    </div>
  );
}
